import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../shared/api';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import RentalSettingsGeneral from './components/RentalSettingsGeneral';
import RentalSettingsCustomer from './components/RentalSettingsCustomer';
import RentalSettingsItems from './components/RentalSettingsItems';
import RentalSettingsPrices from './components/RentalSettingsPrices';
import RentalSettingsHistory from './components/RentalSettingsHistory';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import { CenteredLoader } from '../../components/LoadingSpinner';
import EmptyState from '../../components/feedback/EmptyState';

const RentalSettings = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [rental, setRental] = useState(null);
  // Extract tab from the URL path
  const pathParts = location.pathname.split('/');
  const tab = pathParts[pathParts.length - 1] === 'settings' ||
              isNaN(pathParts[pathParts.length - 1]) === false ?
              'general' : pathParts[pathParts.length - 1];

  const { data: rentalData, isLoading: loading, isError } = useQuery({
    queryKey: ['rental-settings', id],
    queryFn: () => api.get(`rentals/api/rentals/${id}/`),
  });
  const error = isError ? t('rentals.settings.loadError') : null;

  useEffect(() => {
    if (rentalData) {
      setRental(rentalData);
    }
  }, [rentalData]);

  useRealtimeEvents((e) => {
    if (e.model === 'rentals.rental' && String(e.pk) === String(id)) {
      queryClient.invalidateQueries({ queryKey: ['rental-settings', id] });
    }
  });


  if (loading) {
    return (
      <PageShell pretitle={t('rentals.title')} title={t('rentals.settings.title', 'Nastavení zápůjčky')}>
        <PageSection>
          <CenteredLoader />
        </PageSection>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell pretitle={t('rentals.title')} title={t('rentals.settings.title', 'Nastavení zápůjčky')}>
        <PageSection>
          <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>
        </PageSection>
      </PageShell>
    );
  }

  if (!rental) {
    return (
      <PageShell pretitle={t('rentals.title')} title={t('rentals.settings.title', 'Nastavení zápůjčky')}>
        <PageSection>
          <EmptyState
            title={t('rentals.settings.notFound')}
            message={t('rentals.settings.notFoundMessage', 'Zápůjčku se nepodařilo načíst nebo už není dostupná.')}
            action={<Link to="/rentals" className="btn btn-primary">{t('common.back')}</Link>}
          />
        </PageSection>
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('rentals.title')}
      title={t('rentals.settings.rentalId', { id: rental.id })}
      description={t('rentals.settings.subtitle', 'Správa detailů, zákazníka, položek, cen a historie jedné zápůjčky.')}
      actions={
        <>
          <Link to={`/rentals/rental/${id}`} className="btn btn-outline-secondary d-none d-sm-inline-block">
            {t('rentals.settings.back')}
          </Link>
          <Link to={`/rentals/rental/${id}`} className="btn btn-outline-secondary d-sm-none btn-icon" aria-label={t('rentals.settings.back')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-1">
              <path d="M15 6l-6 6l6 6"></path>
            </svg>
          </Link>
        </>
      }
    >
      <PageSection bodyClassName="p-0">
        <Row className="g-0">
          <Col xs={12} md={3} className="border-end">
            <div className="card-body">
                  <h4 className="subheader">{t('rentals.settings.settings')}</h4>
                  <div className="list-group list-group-transparent">
                    <Link
                      to={`/rentals/rental/${id}/settings/general`}
                      className={`list-group-item list-group-item-action d-flex align-items-center ${tab === 'general' ? 'active' : ''}`}
                    >
                      {t('rentals.settings.overview')}
                    </Link>
                    <Link
                      to={`/rentals/rental/${id}/settings/customer`}
                      className={`list-group-item list-group-item-action d-flex align-items-center ${tab === 'customer' ? 'active' : ''}`}
                    >
                      {t('rentals.settings.customerTabName')}
                    </Link>
                    <Link
                      to={`/rentals/rental/${id}/settings/items`}
                      className={`list-group-item list-group-item-action d-flex align-items-center ${tab === 'items' ? 'active' : ''}`}
                    >
                      {t('rentals.settings.itemsTabName')}
                    </Link>
                    <Link
                      to={`/rentals/rental/${id}/settings/prices`}
                      className={`list-group-item list-group-item-action d-flex align-items-center ${tab === 'prices' ? 'active' : ''}`}
                    >
                      {t('rentals.settings.pricingTabName')}
                    </Link>
                    <Link
                      to={`/rentals/rental/${id}/settings/history`}
                      className={`list-group-item list-group-item-action d-flex align-items-center ${tab === 'history' ? 'active' : ''}`}
                    >
                      {t('rentals.settings.historyTabName')}
                    </Link>
                  </div>
            </div>
          </Col>
          <Col xs={12} md={9} className="d-flex flex-column">
            <div className="card-body">
              {tab === 'general' && <RentalSettingsGeneral rental={rental} setRental={setRental} />}
              {tab === 'customer' && <RentalSettingsCustomer rental={rental} setRental={setRental} />}
              {tab === 'items' && <RentalSettingsItems rental={rental} setRental={setRental} />}
              {tab === 'prices' && <RentalSettingsPrices rental={rental} setRental={setRental} />}
              {tab === 'history' && <RentalSettingsHistory rental={rental} />}
            </div>
          </Col>
        </Row>
      </PageSection>
    </PageShell>
  );
};

export default RentalSettings;
